import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import NewChatButton from "@/components/layout/NewChatButton";
import useAdminEval, { EvalBuild, EvalResult } from "@/hooks/admin/useAdminEval";

/** Key metrics to display in the scorecard (ordered by importance). */
const KEY_METRICS: { key: string; label: string; threshold?: number }[] = [
  { key: "sql_validity", label: "SQL Validity", threshold: 0.7 },
  { key: "execution_accuracy", label: "Execution Accuracy", threshold: 0.7 },
  { key: "stat_fidelity", label: "Stat Fidelity", threshold: 0.7 },
  { key: "ves", label: "Verified Execution Score", threshold: 0.7 },
  { key: "conversational_quality", label: "Conversational Quality" },
  { key: "relevance", label: "Relevance" },
  { key: "correctness", label: "Correctness" },
  { key: "faithfulness", label: "Faithfulness" },
];

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function getStatusType(
  status: string
): "success" | "error" | "in-progress" | "warning" | "stopped" | "info" {
  switch (status) {
    case "SUCCEEDED":
      return "success";
    case "FAILED":
      return "error";
    case "IN_PROGRESS":
      return "in-progress";
    case "STOPPED":
      return "stopped";
    case "TIMED_OUT":
      return "warning";
    default:
      return "info";
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case "SUCCEEDED":
      return "Passed";
    case "FAILED":
      return "Failed";
    case "IN_PROGRESS":
      return "Running";
    case "STOPPED":
      return "Stopped";
    case "TIMED_OUT":
      return "Timed Out";
    default:
      return status;
  }
}

function getVerdictType(
  verdict: string
): "success" | "error" | "warning" | "info" {
  switch (verdict) {
    case "PASS":
      return "success";
    case "PASS_WITH_WARNINGS":
      return "warning";
    case "FAIL":
      return "error";
    default:
      return "info";
  }
}

function getVerdictLabel(verdict: string): string {
  switch (verdict) {
    case "PASS":
      return "PASS";
    case "PASS_WITH_WARNINGS":
      return "PASS (with warnings)";
    case "FAIL":
      return "FAIL";
    default:
      return verdict;
  }
}

/** Detail scorecard shown above the table when a run is selected. */
const EvalScorecard: React.FC<{
  build: EvalBuild;
  result: EvalResult;
  reportUrl: string | null;
  downloadUrl: string | null;
  onDismiss: () => void;
}> = ({ build, result, reportUrl, downloadUrl, onDismiss }) => {
  const scores = result.scores;
  const cases = result.cases;

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button variant="icon" iconName="close" onClick={onDismiss} ariaLabel="Close" />
          }
        >
          Run #{build.buildNumber} &mdash;{" "}
          <StatusIndicator type={getVerdictType(result.verdict)}>
            {getVerdictLabel(result.verdict)}
          </StatusIndicator>
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Summary row */}
        <ColumnLayout columns={4} variant="text-grid">
          <Box>
            <Box variant="awsui-key-label">Cases</Box>
            <Box variant="h3">{cases.total}</Box>
            <Box variant="small" color="text-body-secondary">
              {cases.passed} passed &middot; {cases.failed} failed
              {cases.errored > 0 && ` \u00b7 ${cases.errored} errored`}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Duration</Box>
            <Box variant="h3">{formatDuration(build.durationSeconds)}</Box>
            <Box variant="small" color="text-body-secondary">
              {build.startTime ? new Date(build.startTime).toLocaleString() : ""}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Triggered By</Box>
            <Box variant="h3">{build.triggeredBy ?? "Scheduled"}</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Reports</Box>
            <SpaceBetween size="xs">
              {reportUrl && (
                <Link href={reportUrl} external>
                  View HTML Report
                </Link>
              )}
              {downloadUrl && (
                <Link href={downloadUrl} external>
                  Download JSON
                </Link>
              )}
              {!reportUrl && !downloadUrl && (
                <Box color="text-body-secondary">Not available</Box>
              )}
            </SpaceBetween>
          </Box>
        </ColumnLayout>

        {/* Metrics grid — 2 columns of progress bars */}
        <ColumnLayout columns={2}>
          {KEY_METRICS.map(({ key, label, threshold }) => {
            const value = scores[key as keyof typeof scores];
            if (value === undefined || value === null) return null;
            const pct = Math.round(value * 100);
            const meetsThreshold = threshold === undefined || value >= threshold;
            return (
              <ProgressBar
                key={key}
                value={pct}
                status={meetsThreshold ? "success" : "error"}
                label={label}
                description={
                  threshold !== undefined
                    ? `${pct}% (threshold: ${Math.round(threshold * 100)}%)`
                    : `${pct}%`
                }
                resultText={`${pct}%`}
              />
            );
          })}
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
};

const AdminEvaluationPage: React.FC = () => {
  const { builds, loading, triggering, loadStatus, triggerEval } =
    useAdminEval();
  const [selectedBuildId, setSelectedBuildId] = useState<string | null>(null);
  const selectedBuild =
    builds.find((b: EvalBuild) => b.buildId === selectedBuildId) ?? null;

  // Ordered by the "Started" column, most recent run first, which is what this
  // table is for ("Recent Runs") — deliberately not the first-column ordering
  // the other admin tables default to. Sort a copy: `builds` is the hook's
  // state and `Array.prototype.sort` is in-place.
  const orderedBuilds = useMemo(
    () =>
      [...builds].sort((a: EvalBuild, b: EvalBuild) => {
        // A run that has not reported a start time yet sorts after every run
        // that has one, rather than displacing the most recent run.
        if (!a.startTime) return b.startTime ? 1 : 0;
        if (!b.startTime) return -1;
        return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
      }),
    [builds]
  );

  const isRunning = builds.some((b: EvalBuild) => b.status === "IN_PROGRESS");

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Run the evaluation suite against the deployed agent to measure SQL accuracy, conversational quality, and other metrics."
        actions={<NewChatButton />}
      >
        Agent Evaluation
      </Header>

      {/* Scorecard above the table — appears when a completed run is selected */}
      {selectedBuild?.evalResult?.cases && selectedBuild.evalResult.scores && (
        <EvalScorecard
          build={selectedBuild}
          result={selectedBuild.evalResult}
          reportUrl={selectedBuild.reportUrl}
          downloadUrl={selectedBuild.downloadUrl}
          onDismiss={() => setSelectedBuildId(null)}
        />
      )}

      <Table
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  onClick={loadStatus}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  onClick={triggerEval}
                  loading={triggering}
                  disabled={isRunning}
                >
                  {isRunning ? "Evaluation Running" : "Run Evaluation"}
                </Button>
              </SpaceBetween>
            }
            counter={`(${builds.length})`}
          >
            Recent Runs
          </Header>
        }
        loading={loading}
        loadingText="Loading evaluation history..."
        items={orderedBuilds}
        onSelectionChange={({ detail }) => {
          const clickedId = detail.selectedItems[0]?.buildId ?? null;
          setSelectedBuildId(clickedId === selectedBuildId ? null : clickedId);
        }}
        selectedItems={selectedBuild ? [selectedBuild] : []}
        selectionType="single"
        trackBy="buildId"
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="h4">No evaluation runs</Box>
            <Box variant="p" color="text-body-secondary">
              Click &quot;Run Evaluation&quot; to start the first run.
            </Box>
          </Box>
        }
        columnDefinitions={[
          {
            id: "buildNumber",
            header: "#",
            cell: (item: EvalBuild) => `#${item.buildNumber ?? "-"}`,
            width: 70,
          },
          {
            id: "verdict",
            header: "Verdict",
            cell: (item: EvalBuild) => {
              if (item.evalResult) {
                return (
                  <StatusIndicator type={getVerdictType(item.evalResult.verdict)}>
                    {getVerdictLabel(item.evalResult.verdict)}
                  </StatusIndicator>
                );
              }
              return (
                <StatusIndicator type={getStatusType(item.status)}>
                  {getStatusLabel(item.status)}
                </StatusIndicator>
              );
            },
            width: 180,
          },
          {
            id: "startTime",
            header: "Started",
            cell: (item: EvalBuild) =>
              item.startTime
                ? new Date(item.startTime).toLocaleString()
                : "-",
          },
          {
            id: "duration",
            header: "Duration",
            cell: (item: EvalBuild) => formatDuration(item.durationSeconds),
            width: 100,
          },
          {
            id: "triggeredBy",
            header: "Triggered By",
            cell: (item: EvalBuild) => item.triggeredBy ?? "Scheduled",
          },
          {
            id: "actions",
            header: "",
            cell: (item: EvalBuild) => (
              <SpaceBetween direction="horizontal" size="xs">
                {item.reportUrl && (
                  <Link href={item.reportUrl} external variant="primary">
                    Report
                  </Link>
                )}
                {item.logsUrl && (
                  <Link href={item.logsUrl} external>
                    Logs
                  </Link>
                )}
              </SpaceBetween>
            ),
            width: 140,
          },
        ]}
      />
    </SpaceBetween>
  );
};

export default AdminEvaluationPage;
